import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { ConfigProvider, Divider, Steps, Typography, theme } from 'antd'
import {
  Fragment,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const { Title } = Typography

const FONT_SIZE = 16

export type WizardStep = {
  title: string
  subTitle?: string
  /** Prose describing the step. Rendered in the wide middle column. */
  explanation: ReactNode
  /** Inputs and buttons for the step. Rendered in the narrow right column. */
  actions?: ReactNode
}

export type WizardProps = {
  steps: WizardStep[]
}

/** Renders a series of WizardSteps in a single, scrollable view */
export function Wizard({ steps }: WizardProps) {
  const { token } = theme.useToken()
  const [step, setStep] = useState(0)
  const [visited, setVisited] = useState(() => new Set([0]))
  const sectionRefs = useRef<(HTMLElement | null)[]>([])
  const [stepHeights, setStepHeights] = useState<number[]>([])

  // antd exposes no spacing prop for vertical steps, so each step is given the
  // height of the section it points at. The gap between two section tops
  // already includes the divider and the column gap.
  useLayoutEffect(() => {
    const measure = () => {
      const tops = sectionRefs.current.map((section) => section?.offsetTop ?? 0)
      const next = tops
        .slice(0, -1)
        .map((top, index) => (tops[index + 1] ?? top) - top)
      setStepHeights((previous) =>
        previous.length === next.length &&
        previous.every((height, index) => height === next[index])
          ? previous
          : next,
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    for (const section of sectionRefs.current) {
      if (section) {
        observer.observe(section)
      }
    }
    return () => observer.disconnect()
  }, [])

  const stepItems = steps.map((wizardStep, index) => {
    const height = stepHeights[index]
    return {
      title: wizardStep.title,
      subTitle: wizardStep.subTitle,
      status:
        index === step
          ? ('process' as const)
          : visited.has(index)
            ? ('finish' as const)
            : ('wait' as const),
      style: height ? { height } : undefined,
    }
  })

  const goToStep = (next: number) => {
    setStep(next)
    setVisited((previous) => {
      if (previous.has(next)) {
        return previous
      }
      const updated = new Set(previous)
      updated.add(next)
      return updated
    })
    sectionRefs.current[next]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    // The token covers antd's components; the inline size covers the plain
    // prose in each explanation, which antd never styles.
    <ConfigProvider theme={{ token: { fontSize: FONT_SIZE } }}>
      <Vertical flex={1} scroll style={{ fontSize: FONT_SIZE }}>
        {/* Columns are 20% / 60% / 20%: 1:4 here, then 3:1 inside each section.
          flex='0 0 auto' keeps this row at its content height so the wrapper
          above scrolls instead of squashing it. */}
        <Horizontal gap={64} flex='0 0 auto' alignItems='flex-start'>
          <Vertical flex={1} style={{ minWidth: 200 }}>
            <Steps
              direction='vertical'
              current={step}
              onChange={goToStep}
              items={stepItems}
            />
          </Vertical>
          <Vertical flex={3}>
            {steps.map((wizardStep, index) => (
              <Fragment key={wizardStep.title}>
                {index > 0 && <Divider style={{ margin: 0 }} />}
                <section
                  ref={(el) => {
                    sectionRefs.current[index] = el
                  }}
                  onClick={() => goToStep(index)}
                  style={{
                    padding: '32px 16px',
                    borderRadius: token.borderRadius,
                    background:
                      index === step ? token.colorPrimaryBg : undefined,
                  }}
                >
                  <Title level={4} style={{ marginTop: 0 }}>
                    {wizardStep.title}
                  </Title>
                  <Horizontal gap={64} alignItems='flex-start'>
                    <Vertical gap={8} flex={2}>
                      {wizardStep.explanation}
                    </Vertical>
                    <Vertical gap={8} flex={1}>
                      {wizardStep.actions}
                    </Vertical>
                  </Horizontal>
                </section>
              </Fragment>
            ))}
          </Vertical>
        </Horizontal>
      </Vertical>
    </ConfigProvider>
  )
}
